import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useFeaturedLiveshopping from '../../../Data/Liveshopping/useFeaturedLiveshopping';
import PlayButton from '../../../components/LiveShopping/PlayButton';
import useRelativeTime from '../../../components/LiveShopping/useRelativeTime';
import { RenderClientSide } from '../../../components/render';
import { useBabuser } from '../../../hooks/useBambuser';
import { Skeleton } from '../../../styled-components/components/Skeleton';
import useResultUpdater from './useResultUpdater';

import LazyImage from '../../../components/lazy/Image';
import Link from '../../../components/link';
import useRerenderer from '../../../hooks/useRerenderer';

const LiveShopping = () => {
  const { t } = useTranslation(['common', 'startpage']);
  const [_liveShopping] = useFeaturedLiveshopping();
  const liveShopping = useResultUpdater(_liveShopping);
  useRerenderer(60 * 1000);
  const relativeTime = useRelativeTime(liveShopping?.scheduledStartAt, liveShopping?.isLive);

  const register = useBabuser();
  const queue = useRef<{ element: HTMLButtonElement; id: string }[]>([]);
  const onRef = (element: HTMLButtonElement | null, id: string) => {
    if (element) {
      if (register) {
        register(element, id);
      } else {
        queue.current = [...queue.current, { element, id }];
      }
    }
  };

  useEffect(() => {
    if (register) {
      queue.current.forEach((q) => register(q.element, q.id));
    }
  }, [!!register]);

  if (!liveShopping) return null;

  const img = liveShopping.largeCardImage?.file?.url || liveShopping.heroImage?.file?.url;
  const src = img
    ? `url(${liveShopping.largeCardImage?.file?.url || liveShopping.heroImage?.file?.url}?w=${1240}`
    : undefined;

  return (
    <section className="page-section" aria-label={t('Brand live shopping', { ns: 'startpage' })}>
      <Link to={'/liveshopping'} title={t('Show all events', { ns: 'startpage' })}>
        <div data-wx-articlecard="video">
          <LazyImage className="article-card-image" src={src} />
          <div className="article-card-action">
            <RenderClientSide
              skeleton={<Skeleton $variant="circle" $width="80px" $height="80px" />}
            >
              <PlayButton
                liveShopping={liveShopping}
                register={onRef}
                title={t('Show this', { ns: 'common', name: liveShopping.title })}
              />
            </RenderClientSide>
          </div>

          <div className="article-card-body">
            <div data-wx-label="light" className="article-card-pre-heading">
              {t('Brand live shopping', { ns: 'startpage' })}
            </div>
            <div className="article-card-heading">{liveShopping.title}</div>
            <RenderClientSide
              skeleton={
                <div className="article-card-preamble">
                  <Skeleton
                    $width="100px"
                    $height="24px"
                    style={{ display: 'inline-block', verticalAlign: 'bottom' }}
                  />
                </div>
              }
            >
              <div className="article-card-preamble">{relativeTime}</div>
            </RenderClientSide>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default LiveShopping;
